import React, { useEffect, useMemo, useState } from 'react';
import {
  deleteCloverMerchant,
  getCloverAdminSnapshot,
  saveCloverAdminSettings,
  upsertCloverMerchant,
} from '../../services/cloverAdminApi';

const DEFAULT_SETTINGS = {
  enabled: false,
  appId: '',
  webhookAuth: '',
  apiBaseUrl: 'https://api.clover.com',
  autoFetchOrders: true,
  autoMarkReservationsPaid: true,
  reservationCollectionsText: 'reservations\nbookings\nappointments',
};

const INITIAL_MERCHANT_FORM = {
  merchantId: '',
  accessToken: '',
  refreshToken: '',
  environment: 'production',
  inventoryEnabled: false,
  inventoryMode: 'off',
  inventoryEndpointUrl: '',
  inventorySecret: '',
  inventoryKeyVersion: '1',
  inventorySelectionMode: 'printer_labels',
  inventoryDeviceIdsText: '',
};

const statusTone = (status) => {
  switch (String(status || '').toLowerCase()) {
    case 'matched':
      return 'bg-emerald-100 text-emerald-700';
    case 'ignored':
      return 'bg-slate-100 text-slate-700';
    case 'received':
    case 'unmatched':
      return 'bg-amber-100 text-amber-700';
    case 'missing_merchant_token':
      return 'bg-rose-100 text-rose-700';
    default:
      return 'bg-slate-100 text-slate-700';
  }
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('es-PR');
};

const normalizeSettings = (settings = {}) => ({
  enabled: settings.enabled === true,
  appId: settings.appId || '',
  webhookAuth: settings.webhookAuth || '',
  apiBaseUrl: settings.apiBaseUrl || 'https://api.clover.com',
  autoFetchOrders: settings.autoFetchOrders !== false,
  autoMarkReservationsPaid: settings.autoMarkReservationsPaid !== false,
  reservationCollectionsText: Array.isArray(settings.reservationCollections)
    ? settings.reservationCollections.join('\n')
    : DEFAULT_SETTINGS.reservationCollectionsText,
});

const AdminCloverSettings = () => {
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingMerchant, setSavingMerchant] = useState(false);
  const [deletingMerchantId, setDeletingMerchantId] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [snapshot, setSnapshot] = useState({ settings: null, webhook: null, merchants: [] });
  const [settingsForm, setSettingsForm] = useState(DEFAULT_SETTINGS);
  const [merchantForm, setMerchantForm] = useState(INITIAL_MERCHANT_FORM);

  const loadSnapshot = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getCloverAdminSnapshot();
      setSnapshot(data);
      setSettingsForm(normalizeSettings(data?.settings || {}));
    } catch (loadError) {
      console.error('❌ Error cargando configuración Clover:', loadError);
      setError(loadError.message || 'No se pudo cargar Clover.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSnapshot();
  }, []);

  const merchants = snapshot?.merchants || [];
  const webhook = snapshot?.webhook || {};

  const webhookState = useMemo(() => {
    if (!settingsForm.enabled) return 'Deshabilitado';
    if (!webhook?.lastVerificationReceivedAt) return 'Pendiente de verificar';
    return 'Activo';
  }, [settingsForm.enabled, webhook?.lastVerificationReceivedAt]);

  const handleSettingsChange = (event) => {
    const { name, value, type, checked } = event.target;
    setSettingsForm((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleMerchantChange = (event) => {
    const { name, value, type, checked } = event.target;
    setMerchantForm((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSaveSettings = async (event) => {
    event.preventDefault();
    setSavingSettings(true);
    setMessage('');
    setError('');

    try {
      const data = await saveCloverAdminSettings({
        enabled: settingsForm.enabled,
        appId: settingsForm.appId.trim(),
        webhookAuth: settingsForm.webhookAuth.trim(),
        apiBaseUrl: settingsForm.apiBaseUrl.trim(),
        autoFetchOrders: settingsForm.autoFetchOrders,
        autoMarkReservationsPaid: settingsForm.autoMarkReservationsPaid,
        reservationCollections: settingsForm.reservationCollectionsText
          .split('\n')
          .map((entry) => entry.trim())
          .filter(Boolean),
      });

      setSnapshot(data);
      setSettingsForm(normalizeSettings(data?.settings || {}));
      setMessage('Configuración Clover guardada.');
    } catch (saveError) {
      console.error('❌ Error guardando settings Clover:', saveError);
      setError(saveError.message || 'No se pudo guardar la configuración.');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleEditMerchant = (merchant) => {
    setMerchantForm({
      merchantId: merchant.merchantId || '',
      accessToken: '',
      refreshToken: '',
      environment: merchant.environment || 'production',
      inventoryEnabled: merchant.inventoryForwarding?.enabled === true,
      inventoryMode: merchant.inventoryForwarding?.mode || 'off',
      inventoryEndpointUrl: merchant.inventoryForwarding?.endpointUrl || '',
      inventorySecret: '',
      inventoryKeyVersion: merchant.inventoryForwarding?.keyVersion || '1',
      inventorySelectionMode: merchant.inventoryForwarding?.selectionMode || 'printer_labels',
      inventoryDeviceIdsText: (merchant.inventoryForwarding?.posDeviceIds || []).join('\n'),
    });
    setMessage(`Editando merchant ${merchant.merchantId}. Deja tokens en blanco para conservar los actuales.`);
    setError('');
  };

  const handleSaveMerchant = async (event) => {
    event.preventDefault();
    setSavingMerchant(true);
    setMessage('');
    setError('');

    try {
      const data = await upsertCloverMerchant({
        merchantId: merchantForm.merchantId.trim(),
        accessToken: merchantForm.accessToken.trim(),
        refreshToken: merchantForm.refreshToken.trim(),
        environment: merchantForm.environment.trim(),
        inventoryForwarding: {
          enabled: merchantForm.inventoryEnabled,
          mode: merchantForm.inventoryMode,
          endpointUrl: merchantForm.inventoryEndpointUrl.trim(),
          secret: merchantForm.inventorySecret.trim(),
          keyVersion: merchantForm.inventoryKeyVersion.trim() || '1',
          selectionMode: merchantForm.inventorySelectionMode,
          posDeviceIds: merchantForm.inventoryDeviceIdsText.split('\n').map((value) => value.trim()).filter(Boolean),
        },
      });

      setSnapshot(data);
      setMerchantForm(INITIAL_MERCHANT_FORM);
      setMessage('Merchant Clover guardado.');
    } catch (saveError) {
      console.error('❌ Error guardando merchant Clover:', saveError);
      setError(saveError.message || 'No se pudo guardar el merchant.');
    } finally {
      setSavingMerchant(false);
    }
  };

  const handleDeleteMerchant = async (merchantId) => {
    if (!window.confirm(`¿Eliminar merchant ${merchantId}?`)) return;

    setDeletingMerchantId(merchantId);
    setMessage('');
    setError('');
    try {
      const data = await deleteCloverMerchant(merchantId);
      setSnapshot(data);
      if (merchantForm.merchantId === merchantId) {
        setMerchantForm(INITIAL_MERCHANT_FORM);
      }
      setMessage(`Merchant ${merchantId} eliminado.`);
    } catch (deleteError) {
      console.error('❌ Error eliminando merchant Clover:', deleteError);
      setError(deleteError.message || 'No se pudo eliminar el merchant.');
    } finally {
      setDeletingMerchantId('');
    }
  };

  return (
    <div className="space-y-8 p-4 md:p-8 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-8">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Settings · Clover</h1>
          <p className="text-gray-500">
            Configura el webhook global, los merchants conectados y el estado operativo de Clover.
          </p>
        </div>

        {message && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            {message}
          </div>
        )}

        {error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            {error}
          </div>
        )}

        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <p className="text-xs uppercase tracking-[0.18em] text-gray-400">Webhook</p>
            <p className="mt-2 text-lg font-semibold text-gray-900">{webhookState}</p>
            <p className="mt-2 text-sm text-gray-500 break-all">{webhook?.url || '—'}</p>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <p className="text-xs uppercase tracking-[0.18em] text-gray-400">Última Verificación</p>
            <p className="mt-2 text-lg font-semibold text-gray-900">{formatDateTime(webhook?.lastVerificationReceivedAt)}</p>
            <p className="mt-2 text-sm text-gray-500 break-all">{webhook?.lastWebhookVerificationCode || '—'}</p>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <p className="text-xs uppercase tracking-[0.18em] text-gray-400">Último Evento</p>
            <p className="mt-2 text-lg font-semibold text-gray-900">{webhook?.latestEvent?.status || '—'}</p>
            <p className="mt-2 text-sm text-gray-500 break-all">{webhook?.latestEvent?.objectId || '—'}</p>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <p className="text-xs uppercase tracking-[0.18em] text-gray-400">Último Error</p>
            <p className="mt-2 text-sm font-medium text-rose-700">
              {webhook?.lastErrorEvent?.error || webhook?.lastErrorEvent?.status || 'Sin errores recientes'}
            </p>
            <p className="mt-2 text-sm text-gray-500">{formatDateTime(webhook?.lastErrorEvent?.receivedAt)}</p>
          </div>
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Configuración global</h2>
              <p className="text-sm text-gray-500">Estos valores alimentan el webhook global de la app Clover.</p>
            </div>
            <button
              type="button"
              onClick={loadSnapshot}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Refrescar
            </button>
          </div>

          <form className="mt-6 grid gap-4 md:grid-cols-2" onSubmit={handleSaveSettings}>
            <label className="flex items-center gap-3 rounded-xl border border-gray-200 px-4 py-3">
              <input
                type="checkbox"
                name="enabled"
                checked={settingsForm.enabled}
                onChange={handleSettingsChange}
              />
              <span className="text-sm font-medium text-gray-700">Habilitar Clover</span>
            </label>

            <label className="flex items-center gap-3 rounded-xl border border-gray-200 px-4 py-3">
              <input
                type="checkbox"
                name="autoFetchOrders"
                checked={settingsForm.autoFetchOrders}
                onChange={handleSettingsChange}
              />
              <span className="text-sm font-medium text-gray-700">Auto fetch de orders</span>
            </label>

            <label className="flex items-center gap-3 rounded-xl border border-gray-200 px-4 py-3 md:col-span-2">
              <input
                type="checkbox"
                name="autoMarkReservationsPaid"
                checked={settingsForm.autoMarkReservationsPaid}
                onChange={handleSettingsChange}
              />
              <span className="text-sm font-medium text-gray-700">Auto marcar reservaciones como pagadas</span>
            </label>

            <label className="text-sm text-gray-700">
              App ID
              <input
                type="text"
                name="appId"
                value={settingsForm.appId}
                onChange={handleSettingsChange}
                className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2"
              />
            </label>

            <label className="text-sm text-gray-700">
              API Base URL
              <input
                type="text"
                name="apiBaseUrl"
                value={settingsForm.apiBaseUrl}
                onChange={handleSettingsChange}
                className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2"
              />
            </label>

            <label className="text-sm text-gray-700 md:col-span-2">
              Webhook Auth
              <input
                type="password"
                name="webhookAuth"
                value={settingsForm.webhookAuth}
                onChange={handleSettingsChange}
                className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2"
              />
            </label>

            <label className="text-sm text-gray-700 md:col-span-2">
              Reservation Collections
              <textarea
                name="reservationCollectionsText"
                value={settingsForm.reservationCollectionsText}
                onChange={handleSettingsChange}
                rows={4}
                className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2"
                placeholder={'reservations\nbookings\nappointments'}
              />
            </label>

            <div className="md:col-span-2 flex justify-end">
              <button
                type="submit"
                disabled={savingSettings || loading}
                className="rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
              >
                {savingSettings ? 'Guardando...' : 'Guardar configuración'}
              </button>
            </div>
          </form>
        </section>

        <section className="grid gap-6 xl:grid-cols-[1.2fr_1fr]">
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900">Merchants conectados</h2>
            <p className="text-sm text-gray-500">Cada merchant usa el mismo webhook global, pero con su propio token.</p>

            <div className="mt-5 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Merchant</th>
                    <th className="px-4 py-3 text-left font-semibold">Environment</th>
                    <th className="px-4 py-3 text-left font-semibold">Token</th>
                    <th className="px-4 py-3 text-left font-semibold">Webhook</th>
                    <th className="px-4 py-3 text-left font-semibold">Inventario</th>
                    <th className="px-4 py-3 text-left font-semibold">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {merchants.map((merchant) => (
                    <tr key={merchant.merchantId}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{merchant.merchantId}</div>
                        <div className="text-xs text-gray-500">{merchant.source}</div>
                      </td>
                      <td className="px-4 py-3">
                        {merchant.inventoryForwarding?.configured ? (
                          <>
                            <div className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${merchant.inventoryForwarding.enabled && merchant.inventoryForwarding.mode === 'live' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                              {merchant.inventoryForwarding.enabled ? `Inventario ${merchant.inventoryForwarding.mode}` : 'Inventario apagado'}
                            </div>
                            <div className="mt-1 max-w-48 truncate text-xs text-gray-500">{merchant.inventoryForwarding.endpointUrl}</div>
                            <div className={`mt-1 text-xs ${merchant.latestInventoryDelivery?.status === 'failed' ? 'text-rose-700' : 'text-gray-500'}`}>
                              {merchant.latestInventoryDelivery ? `Último envío: ${merchant.latestInventoryDelivery.status} · ${formatDateTime(merchant.latestInventoryDelivery.updatedAt)}` : 'Aún no hay ventas enviadas'}
                            </div>
                            {merchant.latestInventoryDelivery?.lastError && <div className="mt-1 max-w-56 truncate text-xs text-rose-700" title={merchant.latestInventoryDelivery.lastError}>{merchant.latestInventoryDelivery.lastError}</div>}
                          </>
                        ) : (
                          <span className="text-xs text-gray-500">Sin destino configurado</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{merchant.environment || '—'}</td>
                      <td className="px-4 py-3">
                        <div className="text-gray-700">{merchant.tokenPreview || 'Sin token'}</div>
                        <div className="text-xs text-gray-500">
                          {merchant.hasAccessToken ? 'Access token presente' : 'Falta token'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${statusTone(merchant.latestWebhookStatus || (merchant.usable ? 'matched' : 'missing_merchant_token'))}`}>
                          {merchant.latestWebhookStatus || (merchant.usable ? 'usable' : 'sin token')}
                        </div>
                        <div className="mt-1 text-xs text-gray-500">{formatDateTime(merchant.latestWebhookReceivedAt)}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-3 text-xs">
                          <button
                            type="button"
                            onClick={() => handleEditMerchant(merchant)}
                            className="text-blue-600 hover:underline"
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            disabled={deletingMerchantId === merchant.merchantId}
                            onClick={() => handleDeleteMerchant(merchant.merchantId)}
                            className="text-rose-600 hover:underline disabled:opacity-60"
                          >
                            {deletingMerchantId === merchant.merchantId ? 'Eliminando...' : 'Eliminar'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!loading && merchants.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-6 text-center text-gray-500">
                        No hay merchants configurados todavía.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900">Nuevo merchant</h2>
            <p className="text-sm text-gray-500">
              Si editas un merchant existente, puedes dejar tokens vacíos para conservar los actuales.
            </p>

            <form className="mt-5 space-y-4" onSubmit={handleSaveMerchant}>
              <label className="block text-sm text-gray-700">
                Merchant ID
                <input
                  type="text"
                  name="merchantId"
                  value={merchantForm.merchantId}
                  onChange={handleMerchantChange}
                  className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2"
                  required
                />
              </label>

              <label className="block text-sm text-gray-700">
                Access Token
                <input
                  type="password"
                  name="accessToken"
                  value={merchantForm.accessToken}
                  onChange={handleMerchantChange}
                  className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2"
                  placeholder="Pega el token si es nuevo o si quieres reemplazarlo"
                />
              </label>

              <label className="block text-sm text-gray-700">
                Refresh Token
                <input
                  type="password"
                  name="refreshToken"
                  value={merchantForm.refreshToken}
                  onChange={handleMerchantChange}
                  className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2"
                  placeholder="Opcional"
                />
              </label>

              <label className="block text-sm text-gray-700">
                Environment
                <select
                  name="environment"
                  value={merchantForm.environment}
                  onChange={handleMerchantChange}
                  className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2"
                >
                  <option value="production">production</option>
                  <option value="sandbox">sandbox</option>
                </select>
              </label>

              <fieldset className="space-y-3 rounded-xl border border-sky-100 bg-sky-50 p-4">
                <legend className="px-1 text-sm font-semibold text-sky-950">Puente a Inventario Prosseco</legend>
                <p className="text-xs text-sky-800">Es independiente de reservas y del backend del merchant. El secreto no vuelve a mostrarse.</p>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" name="inventoryEnabled" checked={merchantForm.inventoryEnabled} onChange={handleMerchantChange} />
                  Habilitar reenvío de ventas de barra
                </label>
                <label className="block text-sm text-gray-700">
                  Destino HTTPS de Prosseco
                  <input type="url" name="inventoryEndpointUrl" value={merchantForm.inventoryEndpointUrl} onChange={handleMerchantChange} placeholder="https://tu-dominio.com/api/integrations/clover/events" className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2" />
                </label>
                <label className="block text-sm text-gray-700">
                  Secreto compartido
                  <input type="password" name="inventorySecret" value={merchantForm.inventorySecret} onChange={handleMerchantChange} placeholder="Déjalo vacío para conservarlo al editar" className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2" />
                </label>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block text-sm text-gray-700">Modo
                    <select name="inventoryMode" value={merchantForm.inventoryMode} onChange={handleMerchantChange} className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2"><option value="off">off</option><option value="shadow">shadow (sin descontar)</option><option value="live">live (descuenta)</option></select>
                  </label>
                  <label className="block text-sm text-gray-700">Versión de llave
                    <input name="inventoryKeyVersion" value={merchantForm.inventoryKeyVersion} onChange={handleMerchantChange} className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2" />
                  </label>
                </div>
                <label className="block text-sm text-gray-700">Origen de ventas
                  <select name="inventorySelectionMode" value={merchantForm.inventorySelectionMode} onChange={handleMerchantChange} className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2"><option value="printer_labels">Labels de impresora (recomendado)</option><option value="devices">Terminales Clover específicas</option></select>
                </label>
                {merchantForm.inventorySelectionMode === 'devices' && <label className="block text-sm text-gray-700">IDs de terminales (uno por línea)
                  <textarea name="inventoryDeviceIdsText" value={merchantForm.inventoryDeviceIdsText} onChange={handleMerchantChange} rows={3} className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2" />
                </label>}
              </fieldset>

              <div className="flex justify-between gap-3">
                <button
                  type="button"
                  onClick={() => setMerchantForm(INITIAL_MERCHANT_FORM)}
                  className="rounded-xl border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Limpiar
                </button>
                <button
                  type="submit"
                  disabled={savingMerchant || loading}
                  className="rounded-xl bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-60"
                >
                  {savingMerchant ? 'Guardando...' : 'Guardar merchant'}
                </button>
              </div>
            </form>
          </div>
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900">Payload de verificación</h2>
          <pre className="mt-4 overflow-x-auto rounded-xl bg-slate-950 p-4 text-xs text-sky-100">
            {JSON.stringify(webhook?.lastVerificationPayload || {}, null, 2)}
          </pre>
        </section>
      </div>
    </div>
  );
};

export default AdminCloverSettings;
